import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CustomText from '../atoms/custom-text.atom';
import CustomButton from '../atoms/custom-button.atom';
import CustomTextInput from '../atoms/custom-text-input.atom';
import OtpInput from '../molecules/otp-code-input.molecule';
import { FaWhatsapp } from 'react-icons/fa';

export const NumberVerificationTemplate = ({
    isLoading,
    phoneNumber,
    currentStep,
    setCurrentStep,
    onPhoneNumberInput,
    otpCode,
    isPhoneInUse,
    onOtpCodeInput,
    onRequestVerificationCode,
    onRequestVerificationCodeSMS,
}) => {

    return (
        <div style={styles.container}>
            {(() => {
                switch (currentStep) {
                    case 0:
                        return (
                            <PhoneInputStep
                                isLoading={isLoading}
                                phoneNumber={phoneNumber}
                                onPhoneNumberInput={onPhoneNumberInput}
                                isPhoneInUse={isPhoneInUse}
                                setCurrentStep={setCurrentStep}
                                onRequestVerificationCode={onRequestVerificationCode}
                            />
                        );
                    case 1:
                        return (
                            <CodeInputTemplate
                                isLoading={isLoading}
                                otpCode={otpCode}
                                onOtpInput={onOtpCodeInput}
                                handleResendCodeWA={onRequestVerificationCode}
                                handleResendBySMS={onRequestVerificationCodeSMS}
                                setCurrentStep={setCurrentStep}
                            />
                        );
                    case 2:
                        return <ApprovedStep />;
                    default:
                        return null;
                }
            })()}
        </div>
    );
};

const PhoneInputStep = ({
    phoneNumber,
    isPhoneInUse,
    onPhoneNumberInput,
    setCurrentStep,
    onRequestVerificationCode,
    isLoading
}) => {
    const [phoneToShow, setPhoneToShow] = useState(phoneNumber);
    const [loading, setLoading] = useState(false);

    function cleanAndFormatPhoneNumber(text) {
        const cleaned = text.replace('+52', '').replace(/\D/g, '');
        onPhoneNumberInput(cleaned);
        const formatted = cleaned.replace(
            /(\d{2})(\d{4})(\d{0,4})/,
            (match, p1, p2, p3) => `${p1} ${p2} ${p3}`.trim()
        );
        setPhoneToShow(formatted);
    }

    async function handleSendCode() {
        setLoading(true);
        let result = await onRequestVerificationCode();
        if (result) setCurrentStep(1);
        setLoading(false);
    }

    return (
        <div style={styles.phoneInputContainer}>
            <CustomTextInput
                disabled={isLoading}
                value={`+52 ${phoneToShow}`}
                onChange={(e) => cleanAndFormatPhoneNumber(e)}
                maxLength={16}
                placeholder="+52 __ ____ ____"
                label="Número de celular"
                type="tel"
                error={isPhoneInUse ? 'Ya existe una cuenta con ese número' : null}
                right={() => (
                    isPhoneInUse && (
                        <span className="material-icons" style={{ color: '#FF3B30' }}>
                            cancel
                        </span>
                    )
                )}
                id="phone-input"
            />
            <SendCodeButton
                disabled={phoneNumber.length !== 10 || loading || isPhoneInUse || isLoading}
                requestCode={handleSendCode}
                title="Enviar código"
            />
            <CustomText variant="caption" style={{ color: '#626262' }}>
                En caso de que esté equivocado el teléfono con el que te registraste
                inicialmente, puedes ingresar uno nuevo
            </CustomText>
        </div>
    );
};

const CodeInputTemplate = ({
    otpCode,
    onOtpInput,
    numberOfCells = 5,
    setCurrentStep,
    handleResendCodeWA,
    handleResendBySMS,
    isLoading
}) => {
    const [countdown, setCountdown] = useState(30);

    useEffect(() => {
        const timer = setInterval(() => {
            setCountdown((prev) => (prev > 0 ? prev - 1 : 0));
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const handleResendCode = async (type) => {
        if (type === 'sms') {
            await handleResendBySMS();
        } else {
            await handleResendCodeWA();
        }
        setCountdown(30);
    };

    function formatCountdown(seconds) {
        const minutes = Math.floor(seconds / 60)
            .toString()
            .padStart(2, '0');
        const remainingSeconds = (seconds % 60).toString().padStart(2, '0');
        return `${minutes}:${remainingSeconds}`;
    }


    return (
        <div style={styles.codeInputContainer}>
            <OtpInput
                otpCode={otpCode}
                onOtpCodeInput={onOtpInput}
                numberOfCells={numberOfCells}
                additionalInputProps={{id: 'code-input'}}
            />
            <div style={styles.resendContainer}>
                {countdown > 0 ? (
                    <CustomText variant="caption" style={{ color: '#626262' }}>
                        {`Reenviar código en ${formatCountdown(countdown)}`}
                    </CustomText>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                        <SendCodeButton
                            disabled={isLoading}
                            requestCode={() => handleResendCode('wa')}
                            title="Reenviar código"
                        />
                        <CustomText variant="bodySmall" style={{ color: '#626262' }}>
                            ¿No te llega tu código? Puedes intentar recibirlo por SMS
                        </CustomText>
                        <CustomText
                            onClick={() => !isLoading && handleResendCode('sms')}
                            variant="subtitleSmall"
                            style={{ color: '#906F02', cursor: 'pointer' }}
                        >
                            Enviar código por SMS
                        </CustomText>
                        <CustomText
                            variant="subtitleSmall"
                            onClick={() => !isLoading && setCurrentStep(0)}
                            style={{ cursor: 'pointer' }}
                        >
                            Cambiar número de teléfono
                        </CustomText>
                    </div>
                )}
            </div>
        </div>
    );
};

const ApprovedStep = () => {
    return (
        <div style={styles.approvedContainer}>
            <div style={styles.iconContainer}>
                <span className="material-icons" style={{ color: '#358A23' }}>
                    verified
                </span>
            </div>
            <CustomText variant="subtitle" style={{ fontWeight: 'bold' }}>
                Número verificado
            </CustomText>
        </div>
    );
};

const SendCodeButton = ({ disabled = false, title, requestCode }) => {
    return (
        <CustomButton
            style={styles.sendCodeButton}
            variant="yellow"
            disabled={disabled}
            onClick={requestCode}
            right={() => (
                <FaWhatsapp />
            )}
        >
            {title}
        </CustomButton>
    );
};

const styles = {
    container: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 24
    },
    phoneInputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        marginBottom: 32
    },
    codeInputContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 24,
        margin: '0 16px 32px 16px'
    },
    resendContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: 20,
        marginTop: 16
    },
    approvedContainer: {
        margin: '0 16px 32px 16px',
        display: 'flex',
        gap: 16
    },
    iconContainer: {
        display: 'flex',
        alignItems: 'center'
    },
    sendCodeButton: {
        width: '50%'
    },
    whatsappIcon: {
        marginLeft: 8,
        width: 16,
        height: 16
    },
    description: {
        color: '#626262',
        marginBottom: 24
    }
};

NumberVerificationTemplate.propTypes = {
    phoneNumber: PropTypes.string.isRequired,
    onPhoneNumberInput: PropTypes.func.isRequired,
    otpCode: PropTypes.string.isRequired,
    isPhoneInUse: PropTypes.bool,
    onOtpCodeInput: PropTypes.func.isRequired,
    onRequestVerificationCode: PropTypes.func.isRequired,
    onRequestVerificationCodeSMS: PropTypes.func.isRequired
}; 